import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { beneficiairesApi, paiementsApi, type PaiementManuelInput } from '../../services/api';
import { useReferenceData } from '../../hooks/useReferenceData';
import { useToast } from '../../context/ToastContext';
import { Spinner } from '../shared/Spinner';
import { formatApiError } from '../../lib/api-error';
import { BANQUES_VIREMENT } from '../../../server/lib/banques-virement';
import { beneficiairesAffiches, champsManquants, lireMontant, rechercheSansResultat } from '../../lib/saisie-paiement';
import { aujourdhui } from '../../../server/lib/dates';
import {
  COMPTES_INTERNES,
  motifRefusVirementInterne,
  referenceVirementInterne,
} from '../../../server/lib/virement-interne';

/**
 * Saisie d'une facture à payer hors synchro Zoho.
 *
 * Cas d'usage : une facture réglée puis corrigée dans Books (TVA oubliée) dont
 * le solde ne remonte plus, ou une facture qui n'existe pas dans Books. Le
 * motif est obligatoire : c'est ce que le validateur lira pour comprendre
 * pourquoi cette ligne apparaît en dehors du circuit habituel.
 *
 * Le bénéficiaire est choisi dans la liste existante (c'est lui qui porte
 * l'IBAN) : sans lui, la ligne ne serait pas exportable en SEPA.
 *
 * Mode `virement-interne` : transfert entre deux comptes KESTIO. Quatre champs
 * seulement, le serveur retrouve l'IBAN et calcule tout le reste.
 */
export function PaiementManuelModal({
  onClose,
  mode = 'facture',
}: {
  onClose: () => void;
  mode?: 'facture' | 'virement-interne';
}) {
  const interne = mode === 'virement-interne';
  const qc = useQueryClient();
  const toast = useToast();
  const refData = useReferenceData();

  const [beneficiaireId, setBeneficiaireId] = useState('');
  const [search, setSearch] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [banque, setBanque] = useState('');
  const [typeCharge, setTypeCharge] = useState('');
  const [dateFacture, setDateFacture] = useState('');
  const [dateEcheance, setDateEcheance] = useState('');
  const [emettrice, setEmettrice] = useState('');
  const [compteBeneficiaire, setCompteBeneficiaire] = useState('');
  const [dateVirement, setDateVirement] = useState(() => aujourdhui());

  const benefQ = useQuery({
    queryKey: ['beneficiaires', 'fournisseur'],
    queryFn: () => beneficiairesApi.list({ type: 'fournisseur' }),
    staleTime: 60_000,
    enabled: !interne,
  });

  const benefs = useMemo(
    () => beneficiairesAffiches(benefQ.data?.beneficiaires ?? [], search, beneficiaireId),
    [benefQ.data, search, beneficiaireId],
  );
  const benefRetenu = benefs.find((b) => b.id === beneficiaireId);

  const lectureMontant = lireMontant(montant);
  const montantNum = lectureMontant.montant;
  const manquants = champsManquants({ beneficiaireId, numero: billNumber, montant, motif });
  const refusInterne = motifRefusVirementInterne(
    { emettrice, beneficiaire: compteBeneficiaire, montant: montantNum, date: dateVirement },
    aujourdhui(),
  );
  const aCompleter = interne ? refusInterne : manquants.length > 0 ? `Il manque : ${manquants.join(', ')}.` : null;
  const valid = aCompleter === null;

  const createMutation = useMutation({
    mutationFn: (envoi: () => Promise<unknown>) => envoi(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      toast.success(interne ? 'Virement interne ajouté à « À payer ».' : 'Facture ajoutée à « À payer ».');
      onClose();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  function submit() {
    if (!valid || montantNum === null) return;
    if (interne) {
      const virement = { emettrice, beneficiaire: compteBeneficiaire, montant: montantNum, date: dateVirement };
      createMutation.mutate(() => paiementsApi.creerVirementInterne(virement));
      return;
    }
    const facture: PaiementManuelInput = {
      beneficiaire_id: beneficiaireId,
      bill_number: billNumber.trim(),
      montant: montantNum,
      origine_motif: motif.trim(),
      banque: banque || null,
      type_charge: typeCharge || null,
      date_facture: dateFacture || null,
      date_echeance: dateEcheance || null,
      date_paiement: dateEcheance || null,
    };
    createMutation.mutate(() => paiementsApi.creerManuel(facture));
  }

  const champMontant = (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        Montant {interne ? '' : 'à payer '}(€) <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={montant}
        onChange={(e) => setMontant(e.target.value)}
        placeholder={interne ? 'Ex. 5 000,00' : 'Ex. 20,00 pour un solde de TVA'}
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {lectureMontant.erreur && <p className="text-xs text-amber-700 mt-1">{lectureMontant.erreur}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mt-10 mb-10">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold">{interne ? 'Virement interne de trésorerie' : 'Saisir une facture à payer'}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {interne ? (
            <>
              <p className="text-xs text-slate-500">
                Transfert entre deux comptes KESTIO. Il suit le circuit habituel : validation, puis export.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Banque émettrice <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={emettrice}
                    onChange={(e) => setEmettrice(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Choisir dans la liste</option>
                    {BANQUES_VIREMENT.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Banque bénéficiaire <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={compteBeneficiaire}
                    onChange={(e) => setCompteBeneficiaire(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Choisir dans la liste</option>
                    {COMPTES_INTERNES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                {champMontant}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dateVirement}
                    min={aujourdhui()}
                    onChange={(e) => setDateVirement(e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              {refusInterne === null && (
                <p className="text-xs text-slate-600">
                  Référence : <strong>{referenceVirementInterne(dateVirement, emettrice, compteBeneficiaire)}</strong>
                </p>
              )}
            </>
          ) : (
            <>
          <p className="text-xs text-slate-500">
            À utiliser quand la facture ne remonte pas de la synchro Zoho : par exemple un solde
            resté dû après correction de la facture dans Books.
          </p>

          {/* Bénéficiaire */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Bénéficiaire <span className="text-red-500">*</span>
            </label>
            {benefQ.isLoading ? (
              <p className="text-xs text-slate-500 inline-flex items-center gap-2">
                <Spinner /> Chargement des bénéficiaires…
              </p>
            ) : benefQ.isError && !benefQ.data ? (
              // Une liste vide ferait dire « crée-le d'abord », donc créer un doublon
              // d'un bénéficiaire qui existe mais n'a pas pu être lu. Un simple échec
              // de rechargement garde la liste : le bénéficiaire retenu doit rester visible.
              <div className="text-xs text-[#C0423F]">
                <p>Impossible de charger les bénéficiaires : {formatApiError(benefQ.error)}</p>
                <button type="button" onClick={() => benefQ.refetch()} className="mt-1 underline">
                  Réessayer
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un fournisseur…"
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <select
                  value={beneficiaireId}
                  onChange={(e) => setBeneficiaireId(e.target.value)}
                  size={5}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {/* Sans cette option, React surligne d'office le premier nom alors que
                      rien n'est retenu, et cliquer dessus ne déclenche aucun changement. */}
                  <option value="" hidden />
                  {benefs.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.vendor_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-600 mt-1">
                  Bénéficiaire retenu : <strong>{benefRetenu?.vendor_name ?? 'aucun'}</strong>
                </p>
                {rechercheSansResultat(benefQ.data?.beneficiaires ?? [], search) && (
                  <p className="text-xs text-amber-700 mt-1">
                    Aucun bénéficiaire ne correspond : crée-le d'abord dans « Bénéficiaires ».
                  </p>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                N° de facture <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                placeholder="Libellé du virement"
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {champMontant}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Banque</label>
              <select
                value={banque}
                onChange={(e) => setBanque(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choisir dans la liste</option>
                {BANQUES_VIREMENT.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de charge</label>
              <select
                value={typeCharge}
                onChange={(e) => setTypeCharge(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Sans type</option>
                {(refData.data?.charge_types ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date de facture</label>
              <input
                type="date"
                value={dateFacture}
                onChange={(e) => setDateFacture(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Échéance</label>
              <input
                type="date"
                value={dateEcheance}
                onChange={(e) => setDateEcheance(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Pourquoi cette saisie manuelle ? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows={3}
              placeholder="Ex. : TVA oubliée sur la facture, corrigée dans Books après paiement : il reste 20 € à régler."
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Ce texte est affiché au valideur au moment de valider le paiement.
            </p>
          </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {aCompleter && <p className="mr-auto text-xs text-amber-700">{aCompleter}</p>}
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || createMutation.isPending}
            className="px-4 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {createMutation.isPending && <Spinner />}
            Ajouter à « À payer »
          </button>
        </div>
      </div>
    </div>
  );
}
